<template>
  <div>
    <v-container class="mt-0 px-4 pt-0">

      <div class="d-flex align-center px-1 pt-3 pb-3">
        <v-icon color="black" class="mr-2">mdi-frequently-asked-questions</v-icon>
        <span class="text-h6 font-weight-bold black--text">{{ $t(page+'.FAQs') }}</span>
      </div>

      <!-- Loading -->
      <div v-if="!faqs && loading" class="d-flex justify-center align-center py-10">
        <v-progress-circular indeterminate size="64" width="5" color="black"></v-progress-circular>
      </div>

      <!-- Empty state -->
      <div v-else-if="!faqs || !faqs.length" class="empty-state">
        <v-icon size="48" color="grey lighten-1">mdi-help-circle-outline</v-icon>
        <div class="mt-2 grey--text">{{ $t(page+'.No FAQs found') }}</div>
      </div>

      <!-- FAQ list -->
      <div
        v-else
        :style="{ height: (contentHeight+44) + 'px', overflowY: 'auto' }"
        class="faq-scrollable-content"
      >
        <v-expansion-panels flat class="faq-panels">
          <v-expansion-panel
            v-for="(item, i) in faqs"
            :key="i"
            class="faq-panel mb-2"
          >
            <v-expansion-panel-header class="faq-header px-3">
              <div class="d-flex align-start">
                <v-avatar size="24" color="black" class="mr-3 mt-1 flex-shrink-0">
                  <span class="white--text" style="font-size:12px;font-weight:700">Q</span>
                </v-avatar>
                <span class="text-body-2 font-weight-medium">{{ item.Q }}</span>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="faq-content">
              <div class="d-flex align-start pt-2">
                <v-avatar size="24" color="black" outlined class="mr-3 mt-0 flex-shrink-0 avatar-outline">
                  <span class="black--text" style="font-size:12px;font-weight:700">A</span>
                </v-avatar>
                <div class="text-body-2 grey--text text--darken-2 faq-answer" v-html="formatAnswer(item.A)"></div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

    </v-container>

    <v-snackbar v-model="snackbar.active" :color="snackbar.color" bottom>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "Booking",
  props: {
    customerId: {
      type: String,
      default: null
    },
    isActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      page: "Booking",
      stepper: 2,
      wHeight: window.innerHeight,
      wWidth: window.innerWidth,
      contentHeight: 0,
      loading: false,
      faqs: [],
      hasAuthenticated: false,
      snackbar: {
        active: false,
        color: 'success',
        text: ''
      }
    }
  },

  computed: {
    ...mapGetters(['settings', 'countries']),
  },

  watch: {
    isActive: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.hasAuthenticated) {
          this.getFAQs()
        }
      }
    },
  },

  mounted() {
    this.calculateContentHeight()
    window.addEventListener('resize', this.calculateContentHeight)
    window.addEventListener('orientationchange', this.calculateContentHeight)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.calculateContentHeight)
    window.removeEventListener('orientationchange', this.calculateContentHeight)
  },

  methods: {
    calculateContentHeight() {
      this.wHeight = window.innerHeight
      const appBar = document.querySelector('.v-app-bar')
      const appBarHeight = appBar ? appBar.offsetHeight : 50
      const systemBar = document.querySelector('.v-system-bar')
      const systemBarHeight = systemBar ? systemBar.offsetHeight : 0
      const titleHeight = 60
      const containerPadding = 48
      const margins = 32
      this.contentHeight = this.wHeight - appBarHeight - systemBarHeight - titleHeight - containerPadding - margins
      if (this.contentHeight < 300) {
        this.contentHeight = 300
      }
    },

    formatAnswer(text) {
      if (!text) return ''
      return text.replace(/\.\s+/g, '.<br /><br />')
    },

    async getFAQs() {
      this.loading = true
      try {
        const hostname = window.location.hostname
        const payload = JSON.stringify({
          action: 'getFAQs',
          payload: {
            hostname: hostname,
            locale: this.settings.locale
          }
        })

        const res = await this.$http.post('/booking/booking_stepper.php', payload)

        if (res.data.errorid === '0') {
          if (res.data.payloads && res.data.payloads.settings) {
            sessionStorage.setItem('settings', JSON.stringify(res.data.payloads.settings))
            this.$store.dispatch('setSettings', res.data.payloads.settings)
          }

          this.faqs = res.data.payloads.faqs || []
          this.hasAuthenticated = true
          this.loading = false

          this.$nextTick(() => {
            this.calculateContentHeight()
          })
        } else {
          this.showError('Failed to load FAQs')
          this.loading = false
        }
      } catch (error) {
        console.error('Error loading FAQs:', error)
        this.showError('Connection error')
        this.loading = false
      }
    },

    showError(message) {
      this.snackbar = { active: true, color: 'error', text: message }
    },

    showSuccess(message) {
      this.snackbar = { active: true, color: 'success', text: message }
    }
  }
}
</script>

<style scoped>
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

.faq-panels {
  background: transparent;
}

/* Bordered, shadow-free card — matches the outlined tile look */
.faq-panel {
  border: 1.5px solid #e0e0e0 !important;
  border-radius: 16px !important;
  background: #fff;
  overflow: hidden;
}

.faq-panel::before {
  box-shadow: none !important;
}

.faq-header {
  min-height: 56px !important;
}

.faq-header ::v-deep .v-expansion-panel-header__icon .v-icon {
  color: #000 !important;
}

.faq-content {
  background: #fafafa;
  border-top: 1.5px solid #e0e0e0;
}

.faq-answer {
  line-height: 1.6;
}

/* Outlined "A" avatar to keep black as the only accent, no filled amber */
.avatar-outline {
  background: #fff !important;
  border: 1.5px solid #000;
}

/* Scrollable FAQ content area */
.faq-scrollable-content {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
}

.faq-scrollable-content::-webkit-scrollbar {
  width: 8px;
}

.faq-scrollable-content::-webkit-scrollbar-track {
  background: transparent;
}

.faq-scrollable-content::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}
</style>